import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettingsOutlined as AdminIcon,
  CampaignOutlined as CampaignIcon,
  MicOutlined as MicIcon,
  FavoriteBorderOutlined as FavoriteIcon,
  FolderOutlined as DescriptionIcon,
  InsertDriveFileOutlined as FileIcon,
  HomeOutlined as HomeIcon,
  SettingsOutlined as SettingsIcon,
  PeopleOutlined as PeopleIcon,
  BusinessOutlined as BusinessIcon,
  AssignmentOutlined as AssignmentIcon,
  Cancel as CancelIcon,
  MenuBookOutlined as MenuBookIcon,
  QrCode2Outlined as QrCodeIcon,
} from '@mui/icons-material';
import { useAuth, useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';
import { AppColors } from '../theme/colors';
import { SidebarBrand } from './SidebarBrand';
import { switchToCorporateUser as switchToCorporateUserToken } from '../services/accessLinkService';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
  onTabChange?: (tab: 'voice' | 'thanks' | 'theme') => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ open, onClose, onTabChange }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const [isSystemAdminExpanded, setIsSystemAdminExpanded] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleTabChange = (tab: 'voice' | 'thanks' | 'theme') => {
    if (onTabChange) {
      // AdminPageにいる場合、タブを変更
      onTabChange(tab);
    } else {
      // AdminPageにいない場合、AdminPageにナビゲーション（タブ情報をURLパラメータで渡す）
      navigate(`/admin?tab=${tab}`);
    }
    onClose();
  };

  const switchToCorporateUser = async () => {
    // 切り替え中フラグを設定（App.tsxのProtectedRouteで検知される）
    sessionStorage.setItem('switching_user', 'true');
    onClose(); // ドロワーを先に閉じる

    try {
      // 以前は会社パスワードを取得して共有アカウントにログインし直していたが、
      // 会社パスワードは QR/URL 方式では不要になるため、サーバーが発行する
      // カスタムトークンでサインインする形に変更した。
      localStorage.removeItem('adminUserId');
      localStorage.removeItem('loginMethod');
      await switchToCorporateUserToken();

      // 認証状態が更新されるまで待機
      await new Promise(resolve => setTimeout(resolve, 1000));

      sessionStorage.removeItem('switching_user');
      // 切り替え完了フラグを設定（ホーム画面でスナックバーを表示するため）
      sessionStorage.setItem('user_switched', 'true');

      // ページ全体をリロードしてホーム画面へ（認証状態を確実に更新するため）
      window.location.href = '/home';
    } catch (error: any) {
      console.error('ユーザー切り替えエラー:', error);
      sessionStorage.removeItem('switching_user');
      alert(`切り替えに失敗しました: ${error.message}`);
      window.location.href = '/admin-login';
    }
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1300, // ヘッダー（z-index: 1100）より上に表示
        '& .MuiDrawer-paper': {
          width: '65%',
          bgcolor: 'white',
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
        },
      }}
    >
      <Box
        sx={{
          height: 180,
          minHeight: 180,
          flexShrink: 0,
          bgcolor: AppColors.primary,
          p: 2,
          pt: 8,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <Typography
              sx={{
                color: AppColors.primary,
                fontSize: 20,
                fontWeight: 'bold',
              }}
            >
              {user?.displayName ? user.displayName.charAt(0).toUpperCase() : 
               user?.email ? user.email.charAt(0).toUpperCase() : 'A'}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                color: 'black',
                fontSize: 14,
                fontWeight: 400,
              }}
            >
              こんにちは
            </Typography>
            <Typography
              sx={{
                color: 'black',
                fontSize: 16,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {user?.displayName ? user.displayName + 'さん' : user?.email ? user.email + 'さん' : '管理者さん'}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                bgcolor: 'white',
                borderRadius: 2.5,
                border: `1.5px solid ${AppColors.primary}`,
              }}
            >
              <AdminIcon sx={{ fontSize: 10, color: AppColors.primary, mr: 0.5 }} />
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 'bold',
                  color: AppColors.primary,
                }}
              >
                {user?.userType === 'superadmin' ? 'システム管理者' : '管理者'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <List>
        <ListItemButton onClick={() => handleTabChange('voice')} sx={{ px: 3, py: 1 }}>
          <ListItemIcon>
            <MicIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText primary={getBoxTemplate(company?.industryType, 'voice').name} />
        </ListItemButton>
        <ListItemButton onClick={() => handleTabChange('thanks')} sx={{ px: 3, py: 1 }}>
          <ListItemIcon>
            <FavoriteIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText primary={getBoxTemplate(company?.industryType, 'thanks').name} />
        </ListItemButton>
      <ListItemButton onClick={() => handleNavigation('/admin/theme-posts')} sx={{ px: 3, py: 1 }}>
        <ListItemIcon>
          <DescriptionIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
        </ListItemIcon>
        <ListItemText primary={getBoxTemplate(company?.industryType, 'theme').name} />
      </ListItemButton>

        {/* 投稿用のQRコード・URL（坂本様ご指摘④）。配るのは管理者全員の仕事なので、全員に出す */}
        <Divider />
        <ListItemButton onClick={() => handleNavigation('/admin/access-link')} sx={{ px: 3, py: 1 }}>
          <ListItemIcon>
            <QrCodeIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText primary="QRコード・URL" />
        </ListItemButton>
        
        {/* システム管理者セクション（スーパー管理者のみ表示） */}
        {user?.userType === 'superadmin' && (
          <>
            <Divider />
            <ListItemButton
              onClick={() => setIsSystemAdminExpanded(!isSystemAdminExpanded)}
              sx={{ px: 3, py: 1 }}
            >
              <ListItemIcon>
                <AdminIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
              </ListItemIcon>
              <ListItemText
                primary="システム管理者"
                primaryTypographyProps={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: 'black',
                }}
              />
              <Typography sx={{ color: AppColors.primary }}>
                {isSystemAdminExpanded ? '−' : '+'}
              </Typography>
            </ListItemButton>
            
            {/* システム管理者のサブメニュー */}
            {isSystemAdminExpanded && (
              <>
                <ListItemButton
                  onClick={() => handleNavigation('/admin/user-management')}
                  sx={{ px: 6, py: 1 }}
                >
                  <ListItemIcon>
                    <PeopleIcon sx={{ color: AppColors.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="ユーザー管理"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'black',
                    }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => handleNavigation('/admin/theme-management')}
                  sx={{ px: 6, py: 1 }}
                >
                  <ListItemIcon>
                    <MenuBookIcon sx={{ color: AppColors.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="テーマ管理"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'black',
                    }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => handleNavigation('/admin/company-management')}
                  sx={{ px: 6, py: 1 }}
                >
                  <ListItemIcon>
                    <BusinessIcon sx={{ color: AppColors.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="会社管理"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'black',
                    }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => handleNavigation('/admin/audit-log')}
                  sx={{ px: 6, py: 1 }}
                >
                  <ListItemIcon>
                    <AssignmentIcon sx={{ color: AppColors.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="監査ログ"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'black',
                    }}
                  />
                </ListItemButton>
                <ListItemButton
                  onClick={() => handleNavigation('/admin/cancellation')}
                  sx={{ px: 6, py: 1 }}
                >
                  <ListItemIcon>
                    <CancelIcon sx={{ color: AppColors.primary, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="退会処理"
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'black',
                    }}
                  />
                </ListItemButton>
              </>
            )}
          </>
        )}
        
        <Divider />
        <ListItemButton onClick={switchToCorporateUser} sx={{ px: 3, py: 1 }}>
          <ListItemIcon>
            <HomeIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText primary="ホーム画面へ" />
        </ListItemButton>
        <ListItemButton onClick={() => handleNavigation('/settings')} sx={{ px: 3, py: 1 }}>
          <ListItemIcon>
            <SettingsIcon sx={{ color: AppColors.primary, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText primary="設定" />
        </ListItemButton>
      </List>

      <SidebarBrand />
    </Drawer>
  );
};
